// consulta/src/components/MatchConfirmationPage.js
import React, { useState, useEffect } from 'react';
import { ChevronLeft, Zap, Hourglass, ArrowRight, ShieldCheck, SearchX, AlertCircle } from 'lucide-react';
import DoctorIdentityCard from './DoctorIdentityCard';
import OnDemandExplainerModal from './OnDemandExplainerModal';
import { doctorService } from '../services/doctorService';
import specialtyService from '../services/specialtyService';
import { getDoctorSchedule } from '../services/scheduleService';
import GlobalLoader from './GlobalLoader';

/**
 * Pantalla de checkout con la doctora recomendada y su slot pre-seleccionado.
 *
 * URL: /agendar-match?doctor=<id>&specialty=<name>&slot_date=YYYY-MM-DD
 *      &slot_hour=HH:MM&slot_mode=immediate|on_demand&duration_id=<id>
 *      &propose_slot=1 (alternativa cuando no hay slot)
 *
 * Props:
 *  - onConfirm(slot, doctor, specialty) — callback al CTA principal.
 *      Reusa proceedWithSlot del App.js.
 *  - onMoreSchedules(doctorId, specialtyName) — callback "← Más horarios".
 *      Navega a /horarios-doctora?id=X&specialty=Y.
 *  - onProposeSlot(doctor) — callback cuando no hay slot disponible.
 */
const MatchConfirmationPage = ({ onConfirm, onMoreSchedules, onProposeSlot }) => {
  const [doctor, setDoctor] = useState(null);
  const [specialty, setSpecialty] = useState(null);
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOnDemandDialog, setShowOnDemandDialog] = useState(false);

  useEffect(() => {
    const load = async () => {
      const params = new URLSearchParams(window.location.search);
      const doctorIdParam = parseInt(params.get('doctor'), 10);
      const specialtyName = params.get('specialty');
      const proposeSlot = params.get('propose_slot') === '1';
      const slotDate = params.get('slot_date');
      const slotHour = params.get('slot_hour');
      const slotModeParam = params.get('slot_mode') || 'immediate';

      if (!doctorIdParam) {
        setError('doctor-not-found');
        setLoading(false);
        return;
      }

      try {
        // 1. Resolver specialty desde el nombre
        const allSpecs = await specialtyService.getFormattedOnDemandSpecialties();
        let spec = null;
        if (specialtyName) {
          const norm = specialtyName.trim().toLowerCase();
          spec = allSpecs.find(s => (s.name || '').trim().toLowerCase() === norm);
        }

        // 2. Resolver doctora
        let dr = null;
        if (spec) {
          const doctors = await doctorService.getDoctorsBySpecialty(spec.id);
          dr = doctors.find(d => d.id === doctorIdParam);
        }
        // Fallback: buscar en todas las specialties si no se encontró por nombre
        if (!dr) {
          for (const s of allSpecs) {
            const doctors = await doctorService.getDoctorsBySpecialty(s.id);
            const found = doctors.find(d => d.id === doctorIdParam);
            if (found) {
              dr = found;
              spec = spec || s;
              break;
            }
          }
        }

        if (!dr) {
          setError('doctor-not-found');
          setLoading(false);
          return;
        }

        // 3. Cargar config completo de specialty (durations)
        const configMap = await specialtyService.getSpecialtyConfig(spec.id);
        const fullSpec = configMap?.[spec.id] || spec;

        setDoctor(dr);
        setSpecialty(fullSpec);

        // 4. Resolver slot — Task C4 below
        if (proposeSlot) {
          setSlot(null);
        } else if (slotDate && slotHour) {
          const durationId = fullSpec?.allDurations?.[0]?.id || parseInt(params.get('duration_id'), 10);
          if (!durationId) {
            setError('missing-duration');
            setLoading(false);
            return;
          }
          const today = new Date().toISOString().split('T')[0];
          const end = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          const allSlots = await getDoctorSchedule(dr.id, today, end, durationId);
          const availableSlots = allSlots.filter((s) => s.available !== false);

          // Match progresivo:
          // 1. Exact: misma fecha + misma hora
          // 2. Same day: misma fecha (caso típico — el landing usó 10min blocks pero el slot real es 60min con start time distinto)
          // 3. Cualquiera: primer slot disponible (último recurso)
          // Si llegamos al recurso 2 o 3 lo marcamos para que el UI lo refleje.
          let matched = availableSlots.find((s) =>
            s.date === slotDate &&
            (s.hour === slotHour || s.init === slotHour),
          );
          let adjusted = false;
          if (!matched) {
            matched = availableSlots.find((s) => s.date === slotDate);
            if (matched) adjusted = true;
          }
          if (!matched) {
            matched = availableSlots[0];
            if (matched) adjusted = true;
          }
          if (!matched) {
            setError('slot-taken');
            setLoading(false);
            return;
          }
          if (adjusted && process.env.NODE_ENV !== 'production') {
            console.warn(
              '[MatchConfirmationPage] slot ajustado',
              { requested: { slotDate, slotHour }, actual: { date: matched.date, hour: matched.hour || matched.init } },
            );
          }
          setSlot({
            ...matched,
            booking_mode: matched.booking_mode || slotModeParam,
            hour: matched.hour || matched.init,
            adjustedFromSuggested: adjusted,
          });
        } else {
          setError('missing-slot-params');
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('[MatchConfirmationPage] load error:', err);
        setError('load-failed');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <GlobalLoader title="Preparando tu cita" subtitle="Confirmando el horario sugerido" />;
  }

  // Read URL params again for error/back UI (Task C3 will refactor this once doctor is loaded)
  const params = new URLSearchParams(window.location.search);
  const doctorId = parseInt(params.get('doctor'), 10);
  const specialtyName = params.get('specialty');

  if (error === 'doctor-not-found') {
    return (
      <div className="min-h-screen bg-warm-canvas flex items-center justify-center p-6">
        <div className="bg-surface-card rounded-card p-8 max-w-md text-center">
          <SearchX className="mx-auto mb-4 text-brand/50" size={44} aria-hidden="true" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            No encontramos a tu doctora
          </h3>
          <p className="text-text-secondary text-sm mb-6">
            Pero hay otras esperándote.
          </p>
          <button
            onClick={() => onMoreSchedules(null, specialtyName)}
            className="px-6 py-3 bg-daisies text-white rounded-card font-bold hover:bg-daisies/90 transition"
          >
            Ver doctoras disponibles
          </button>
        </div>
      </div>
    );
  }

  if (error === 'slot-taken') {
    return (
      <div className="min-h-screen bg-warm-canvas flex items-center justify-center p-6">
        <div className="bg-surface-card rounded-card p-8 max-w-md text-center">
          <Hourglass className="mx-auto mb-4 text-brand/50" size={44} aria-hidden="true" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            Este horario ya no está libre
          </h3>
          <p className="text-text-secondary text-sm mb-6">
            Alguien más lo agendó. Pero hay otros disponibles.
          </p>
          <button
            onClick={() => onMoreSchedules(doctorId, specialtyName)}
            className="px-6 py-3 bg-daisies text-white rounded-card font-bold hover:bg-daisies/90 transition"
          >
            Ver otros horarios
          </button>
        </div>
      </div>
    );
  }

  if (error === 'missing-duration' || error === 'missing-slot-params' || error === 'load-failed') {
    return (
      <div className="min-h-screen bg-warm-canvas flex items-center justify-center p-6">
        <div className="bg-surface-card rounded-card p-8 max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 text-brand/50" size={44} aria-hidden="true" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            No pudimos cargar tu match
          </h3>
          <p className="text-text-secondary text-sm mb-6">
            Algo salió mal. Prueba ver más horarios disponibles.
          </p>
          <button
            onClick={() => onMoreSchedules(doctorId, specialtyName)}
            className="px-6 py-3 bg-daisies text-white rounded-card font-bold hover:bg-daisies/90 transition"
          >
            Ver horarios disponibles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-canvas">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-black/5 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onMoreSchedules(doctorId, specialtyName)}
          className="p-2 -ml-2 rounded-full hover:bg-surface transition flex items-center gap-1 text-sm font-semibold text-text-secondary hover:text-daisies"
          aria-label="Más horarios"
        >
          <ChevronLeft size={20} />
          Más horarios
        </button>
      </header>

      <main className="px-4 py-5 max-w-md mx-auto">
        {/* Hero: doctora */}
        <div className="bg-white rounded-card shadow-elevated p-6 mb-6 relative">
          <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-daisies text-white text-xs font-bold rounded-full px-3 py-1 shadow-soft">
            <ShieldCheck size={13} aria-hidden="true" />
            Match para ti
          </div>
          <DoctorIdentityCard doctor={doctor} size="hero" showStats={false} />
        </div>

        {/* Slot info — o empty state si propose_slot */}
        {!slot ? (
          <div className="bg-amber-pill/20 rounded-card p-5 mb-6">
            <p className="text-sm font-semibold text-amber-900 mb-2">
              {doctor.name} no tiene horarios esta semana
            </p>
            <p className="text-xs text-amber-800 mb-4">
              Pero puedes proponer una fecha que te acomode. La doctora confirma en 24h.
            </p>
            <button
              onClick={() => onProposeSlot(doctor)}
              className="w-full py-3 bg-amber-500 text-white rounded-card font-bold hover:bg-amber-600 transition flex items-center justify-center gap-2"
            >
              Proponer horario
              <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-card shadow-soft p-5 mb-6">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
                Tu horario sugerido
              </p>
              <p className="text-3xl font-bold text-text-primary tabular-nums mb-1">
                {formatHumanDate(slot.date)} a las {slot.hour || slot.init}
              </p>
              <div className="flex items-center gap-2 flex-wrap mt-3">
                {slot.booking_mode === 'on_demand' ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">
                    <Hourglass size={12} />
                    Bajo confirmación
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-daisies-50 text-daisies">
                    <Zap size={12} />
                    Disponibilidad inmediata
                  </span>
                )}
                {slot.is_locked && slot.booking_mode === 'immediate' && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                    ✓ Garantizado por Daisies
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-surface text-text-secondary">
                  {durationLabelFor(specialty)} min
                </span>
              </div>
              {slot.booking_mode === 'on_demand' && (
                <p className="text-xs text-text-secondary mt-3 leading-relaxed">
                  Al confirmar enviaremos tu solicitud. La doctora tiene 24h para aceptar.
                </p>
              )}
            </div>

            <button
              onClick={() => {
                if (slot.booking_mode === 'on_demand') {
                  // Check explainer dismissed within 30 days
                  const seen = localStorage.getItem('daisies_seen_on_demand_explainer');
                  const seenTs = seen ? parseInt(seen, 10) : 0;
                  const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
                  if (!seen || (Date.now() - seenTs) > THIRTY_DAYS) {
                    setShowOnDemandDialog(true);
                    return;
                  }
                }
                onConfirm(slot, doctor, specialty);
              }}
              className="w-full py-4 bg-daisies text-white rounded-card font-bold text-lg hover:bg-daisies/90 transition flex items-center justify-center gap-2 shadow-elevated"
            >
              {slot.booking_mode === 'on_demand' ? 'Enviar solicitud' : 'Agendar ahora'}
              <ArrowRight size={18} />
            </button>
          </>
        )}

        <div className="text-center mt-6">
          <button
            onClick={() => onMoreSchedules(doctorId, specialtyName)}
            className="text-sm text-text-secondary hover:text-daisies transition block w-full"
          >
            Ver otras opciones de {doctor.name} →
          </button>
        </div>
      </main>

      {showOnDemandDialog && (
        <OnDemandExplainerModal
          slot={slot}
          doctor={doctor}
          onAccept={() => {
            setShowOnDemandDialog(false);
            localStorage.setItem('daisies_seen_on_demand_explainer', Date.now().toString());
            onConfirm(slot, doctor, specialty);
          }}
          onCancel={() => {
            setShowOnDemandDialog(false);
            onMoreSchedules(doctorId, specialtyName);
          }}
        />
      )}
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatHumanDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const compareDate = new Date(d); compareDate.setHours(0, 0, 0, 0);
  if (compareDate.getTime() === today.getTime()) return 'Hoy';
  if (compareDate.getTime() === tomorrow.getTime()) return 'Mañana';
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${days[d.getDay()]} ${d.getDate()} de ${months[d.getMonth()]}`;
}

function durationLabelFor(specialty) {
  const dur = specialty?.allDurations?.[0];
  return dur?.value || dur?.duration_minutes || '60';
}

export default MatchConfirmationPage;
